/**
 * LeftRail — navegación principal colapsable.
 *
 * Renderiza lo que recibe del descriptor:
 *  - Historial de chats (descriptor.chats)
 *  - Agentes disponibles (descriptor.agents_visibles)
 *  - Menú de secciones (descriptor.capabilities.menu) — data-driven
 *
 * R-PT-02: data-driven. No asume secciones — pinta capabilities.menu.
 * R-PT-06: lista de chats con "de qué va" y agentes implicados.
 */

import { useState } from 'react'
import { MessageSquare, Users, FileText, FolderOpen, ShieldCheck, Bookmark, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { useSession } from '../context/SessionContext.jsx'
import { AgentAvatar } from './AgentAvatar.jsx'
import { TenantLogo } from './TenantLogo.jsx'
import { agentColorOf } from '../lib/agent-colors.js'
import { avatarUrlOf } from '../lib/images.js'
import { groupByDepartment } from '../lib/agent-groups.js'

// #459: iconografía de línea (lucide) en vez de emojis Unicode. Se mapea el
// `icon` del descriptor (capabilities.menu[].icon) a un componente; sin match,
// un icono neutro de reserva. Heredan currentColor (siguen el color del texto).
const SECTION_ICONS = {
  chat:    MessageSquare,
  agents:  Users,
  reports: FileText,
  areas:   FolderOpen,
  files:   FolderOpen,   // #323: visor de ficheros de la ZFT
  admin:   ShieldCheck,
}
const FALLBACK_ICON = Bookmark
const NAV_ICON_PROPS = { size: 17, strokeWidth: 1.8, 'aria-hidden': true, className: 'rail-section__ico' }

function Chevron({ open }) {
  const Ico = open ? ChevronDown : ChevronRight
  return <Ico size={16} strokeWidth={1.9} aria-hidden="true" />
}

function formatRelativeTime(isoString) {
  const t = new Date(isoString).getTime()
  if (Number.isNaN(t)) return ''            // #277: caché sin updated_at
  const diff  = Date.now() - t
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins < 60)  return `hace ${mins}m`
  if (hours < 24) return `hace ${hours}h`
  return `hace ${days}d`
}

function ChatItem({ chat, active, onClick, onDelete }) {
  // #277: un chat del descriptor puede llegar parcial (US-62: la lista viene
  // del bootstrap y un chat puede no traer `de_que_va`/`agentes_en_chat`).
  // Guardamos los accesos para que pintar un chat incompleto no rompa el rail.
  const deQueVa = chat.de_que_va ?? ''
  const agentes = (chat.agentes_en_chat ?? []).map(a => a.nombre).join(', ')
  const titulo  = chat.titulo ?? '(sin título)'
  return (
    // US-467: fila con el botón de selección + el de borrar como HERMANOS (nada
    // de <button> anidados, HTML inválido). El fondo hover/activo vive en la
    // fila para que no parpadee al pasar sobre el icono de borrar.
    <div className={'chat-item-row' + (active ? ' chat-item-row--active' : '')}>
      <button
        className={'chat-item' + (active ? ' chat-item--active' : '')}
        onClick={onClick}
        title={deQueVa}
      >
        <span className="chat-item__title">{titulo}</span>
        <span className="chat-item__meta">
          {agentes}{agentes && ' · '}{formatRelativeTime(chat.updated_at)}
        </span>
        <span className="chat-item__desc">{deQueVa}</span>
      </button>
      {onDelete && (
        <button
          type="button"
          className="chat-item__delete"
          aria-label={`Eliminar conversación ${titulo}`}
          title="Eliminar conversación"
          // Detener la propagación: borrar no debe seleccionar/abrir el chat.
          onClick={e => { e.stopPropagation(); onDelete(chat) }}
        >
          <Trash2 size={15} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

function AgentCard({ agent, ringColor, onClick }) {
  const puedeIniciarChat = agent.puede_iniciar_chat !== false
  return (
    <button
      className={'agent-card' + (puedeIniciarChat ? '' : ' agent-card--disabled')}
      onClick={puedeIniciarChat ? onClick : undefined}
      disabled={!puedeIniciarChat}
      title={agent.descripcion}
    >
      <span className="agent-card__avatar" style={ringColor ? { '--dot-color': ringColor } : undefined}>
        <AgentAvatar name={agent.nombre} avatarUrl={avatarUrlOf(agent)} size={36} ringColor={ringColor} />
        {puedeIniciarChat && <span className="agent-card__dot" />}
      </span>
      <span className="agent-card__info">
        <span className="agent-card__name">{agent.nombre}</span>
        <span className="agent-card__role">{agent.rol}</span>
      </span>
    </button>
  )
}

export default function LeftRail({
  collapsed,
  activeChatId,
  activeView = 'chat',
  onSelectChat,
  onNewChat,
  onSelectAgent,
  onNavigate,
  onDeleteChat,
}) {
  const { chats, agents, capabilities, agentPalette, resolvedTheme, user, tenant } = useSession()
  const [expandedSection, setExpandedSection] = useState('chats')
  const [search, setSearch] = useState('')

  const menu = capabilities?.menu ?? []

  // Las secciones nativas (chats / agentes) tienen cuerpo propio (buscador,
  // tarjetas), pero su ETIQUETA también es data-driven (R-PT-02): se toma del
  // ítem del descriptor por id, con un default si el tenant no lo trae.
  const menuById   = Object.fromEntries(menu.map(m => [m.id, m]))
  const chatsLabel  = menuById.chats?.label   ?? 'Conversaciones'
  const agentsLabel = menuById.agentes?.label ?? 'Agentes'

  // Filtrar chats y agentes por búsqueda
  const searchLower = search.toLowerCase()
  const filteredChats = search
    ? chats.filter(c =>
        (c.titulo ?? '').toLowerCase().includes(searchLower) ||
        (c.de_que_va ?? '').toLowerCase().includes(searchLower)
      )
    : chats
  const filteredAgents = search
    ? agents.filter(a =>
        a.nombre.toLowerCase().includes(searchLower) ||
        a.rol.toLowerCase().includes(searchLower)
      )
    : agents

  function toggleSection(id) {
    setExpandedSection(s => (s === id ? null : id))
  }

  if (collapsed) return null

  const activos = agents.filter(a => a.puede_iniciar_chat !== false).length
  // Mostrador del equipo: solo los agentes DISPONIBLES (operativos), agrupados
  // por departamento (data-driven). Coherente con la entrada — «solo los
  // disponibles» (Guo 2026-07-16); hoy en DKtec, solo Anne.
  const agentGroups = groupByDepartment(
    filteredAgents.filter(a => a.puede_iniciar_chat !== false),
  )

  return (
    <aside className="left-rail">
      {/* Marca del Pórtico (Gabinete: la marca vive en el rail, no en la TopBar).
          El logo lo resuelve el front por tenant.id y tema (GEN-11, lib/images.js);
          sin asset, TenantLogo cae a la insignia con la inicial. */}
      <div className="left-rail__brand">
        <TenantLogo tenant={tenant} theme={resolvedTheme} height={26} />
        <span className="left-rail__brand-name">Pórtico</span>
      </div>

      {/* Búsqueda global */}
      <div className="left-rail__search">
        <input
          type="search"
          className="rail-search"
          placeholder="Buscar chats, agentes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Buscar"
        />
      </div>

      {/* Nuevo chat */}
      <button className="new-chat-btn" onClick={onNewChat}>
        <span className="new-chat-btn__icon">+</span>
        Nueva conversación
      </button>

      {/* Sección: Conversaciones */}
      <div className="rail-section">
        <button
          className="rail-section__header"
          onClick={() => toggleSection('chats')}
          aria-expanded={expandedSection === 'chats'}
        >
          <span className="rail-section__label"><MessageSquare {...NAV_ICON_PROPS} /> {chatsLabel}</span>
          <span className="rail-section__arrow">
            <Chevron open={expandedSection === 'chats'} />
          </span>
        </button>
        {expandedSection === 'chats' && (
          <div className="rail-section__body">
            {filteredChats.length === 0 && (
              <p className="rail-empty">Sin conversaciones</p>
            )}
            {filteredChats.map(c => (
              <ChatItem
                key={c.chat_id}
                chat={c}
                active={c.chat_id === activeChatId}
                onClick={() => onSelectChat(c.chat_id)}
                onDelete={onDeleteChat}
              />
            ))}
          </div>
        )}
      </div>

      {/* Mostrador del equipo — SIEMPRE visible, debajo de Conversaciones
          (Gabinete: «tu equipo siempre a la vista»). Fotos + nombre + rol,
          agrupados por departamento; un clic abre un chat dirigido al agente.
          La etiqueta ("Agentes"/"Asistentes") sigue siendo data-driven (R-PT-02). */}
      <div className="rail-section rail-mostrador">
        <div className="rail-section__header rail-section__header--static">
          <span className="rail-section__label"><Users {...NAV_ICON_PROPS} /> {agentsLabel}</span>
          <span className="rail-section__count">{activos} activos</span>
        </div>
        <div className="rail-section__body">
          {agentGroups.length === 0 && <p className="rail-empty">Sin agentes disponibles</p>}
          {agentGroups.map(grupo => (
            <div className="rail-mostrador__group" key={grupo.departamento}>
              <span className="rail-mostrador__dept">{grupo.departamento}</span>
              {grupo.agents.map(a => (
                <AgentCard
                  key={a.id ?? a.nombre}
                  agent={a}
                  ringColor={agentColorOf(agentPalette ?? {}, a.id, resolvedTheme)}
                  onClick={() => onSelectAgent(a)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Secciones adicionales data-driven desde capabilities.menu.
          Un ítem habilitado navega a su vista (onNavigate); uno deshabilitado
          se muestra como "Próx." sin acción. US-26 usa esto para el panel admin. */}
      {menu
        .filter(item => !['chats', 'agentes'].includes(item.id) && item.visible)
        .map(item => {
          const Ico = SECTION_ICONS[item.icon] ?? FALLBACK_ICON
          return (
          <div key={item.id} className="rail-section">
            <button
              className={
                'rail-section__header'
                + (!item.enabled ? ' rail-section__header--disabled' : '')
                + (item.enabled && activeView === item.id ? ' rail-section__header--active' : '')
              }
              onClick={() => item.enabled && onNavigate?.(item.id)}
              aria-current={activeView === item.id ? 'page' : undefined}
              disabled={!item.enabled}
              title={!item.enabled ? 'Próximamente' : undefined}
            >
              <span className="rail-section__label">
                <Ico {...NAV_ICON_PROPS} /> {item.label}
              </span>
              {!item.enabled && (
                <span className="rail-section__badge">Próx.</span>
              )}
            </button>
          </div>
          )
        })}

      {/* Pie de owner — nuevo (Gabinete): identidad del usuario de la sesión,
          data-driven (badge = user.rol tal cual lo proyecta el descriptor;
          nunca se decide en el front, R-PT-02). */}
      {user && (
        <div className="left-rail__owner">
          <AgentAvatar name={user.display_name} size={30} />
          <span className="left-rail__owner-info">
            <span className="left-rail__owner-name">{user.display_name}</span>
            {user.rol && (
              <span className="agent-role-badge">{user.rol}</span>
            )}
          </span>
        </div>
      )}
    </aside>
  )
}
